/**
 * JWT utility functions for token parsing and validation
 */

export interface JWTPayload {
  exp: number;
  iat: number;
  sub: string;
  [key: string]: any;
}

/**
 * Parse JWT token and extract payload
 * @param token JWT token string
 * @returns Parsed JWT payload or null if invalid
 */
export const parseJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param token JWT token string
 * @returns true if token is expired, false otherwise
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * Get token expiration time in milliseconds
 * @param token JWT token string
 * @returns Expiration time in milliseconds or null if invalid
 */
export const getTokenExpiration = (token: string): number | null => {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000; // Convert to milliseconds
};

/**
 * Check if token will expire within the next N minutes
 * @param token JWT token string
 * @param minutes Number of minutes to check ahead
 * @returns true if token expires within the specified minutes
 */
export const isTokenExpiringSoon = (token: string, minutes: number = 5): boolean => {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationBuffer = minutes * 60; // Convert minutes to seconds
  
  return payload.exp < (currentTime + expirationBuffer);
};
